import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { dict, LANGS } from "./translations";

const LangCtx = createContext({ lang: "uk", setLang: () => {}, t: (k) => k });

const EN_COUNTRIES = new Set(["US", "GB", "CA", "AU", "NZ", "IE", "SG"]);

const detectFromBrowser = () => {
  try {
    const nav = (navigator.language || "").toLowerCase();
    if (nav.startsWith("en")) return "en";
  } catch (e) { /* noop */ }
  return "uk";
};

const detectByGeo = async () => {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 2000);
    const r = await fetch("https://ipapi.co/json/", { signal: ctrl.signal });
    clearTimeout(timer);
    if (!r.ok) return detectFromBrowser();
    const d = await r.json();
    const cc = (d.country_code || "").toUpperCase();
    if (EN_COUNTRIES.has(cc)) return "en";
    return "uk";
  } catch {
    return detectFromBrowser();
  }
};

const TAB_TITLES = {
  uk: 'STUDYGRI — Освіта за кордоном · Школи та університети світу',
  en: 'STUDYGRI — Study abroad · Top schools & universities worldwide',
  ru: 'STUDYGRI — Образование за границей · Школы и университеты мира',
  ar: 'STUDYGRI — الدراسة بالخارج · أفضل المدارس والجامعات في العالم',
  it: 'STUDYGRI — Studiare all\u2019estero · Migliori scuole e università al mondo',
  zh: 'STUDYGRI — 出国留学 · 全球顶尖学校与大学',
  es: 'STUDYGRI — Estudia en el extranjero · Mejores escuelas y universidades del mundo',
  pl: 'STUDYGRI — Studia za granicą · Najlepsze szkoły i uczelnie świata'
};

export const LangProvider = ({ children, basename = "" }) => {
  // Language from URL path prefix (e.g. /ua/team → 'ua' → normalized to 'uk')
  // Accept `ua` as alias for Ukrainian even though ISO code is `uk`
  const normalizePathLang = (l) => (l === "ua" ? "uk" : l);
  const urlLang = basename ? normalizePathLang(basename.slice(1)) : null;

  // Subdomain override: eng.* → force English regardless of path/localStorage/geo
  const subdomainLang = (() => {
    if (typeof window === "undefined") return null;
    const host = window.location.hostname.toLowerCase();
    if (host.startsWith("eng.")) return "en";
    return null;
  })();

  const stored = typeof window !== "undefined" ? localStorage.getItem("sg_lang") : null;
  const valid = LANGS.map(l => l.code);
  const initial = subdomainLang
    ? subdomainLang
    : (urlLang && valid.includes(urlLang)
        ? urlLang
        : (stored && valid.includes(stored) ? stored : "uk"));
  const [lang, setLang] = useState(initial);
  const [manual, setManual] = useState(!!subdomainLang || !!urlLang || !!stored);

  // Run geo-detect once on first visit (no url prefix, no stored choice)
  useEffect(() => {
    if (manual) return;
    let cancelled = false;
    detectByGeo().then((l) => { if (!cancelled) setLang(l); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = TAB_TITLES[lang] || TAB_TITLES.uk;
  }, [lang]);

  // Switch language → reload page with new path prefix so clean URLs propagate everywhere
  const setLangManual = (l) => {
    if (typeof window === "undefined") { setLang(l); return; }
    localStorage.setItem("sg_lang", l);
    // If on 'eng.*' subdomain and switching to non-English → go back to main domain
    const host = window.location.hostname.toLowerCase();
    if (host.startsWith("eng.") && l !== "en") {
      const mainHost = host.replace(/^eng\./, "");
      const urlSeg = l === "uk" ? "ua" : l;
      const nextUrl = `${window.location.protocol}//${mainHost}/${urlSeg}${window.location.search}${window.location.hash}`;
      window.location.assign(nextUrl);
      return;
    }
    // Preserve current inner path (after existing basename)
    const innerPath = window.location.pathname.startsWith(basename + "/") || window.location.pathname === basename
      ? window.location.pathname.slice(basename.length) || "/"
      : window.location.pathname;
    // 'uk' → use `ua` in URL (user preference), others as ISO code
    const urlSeg = l === "uk" ? "ua" : l;
    const nextPath = `/${urlSeg}${innerPath === "/" ? "" : innerPath}`;
    window.location.assign(nextPath + window.location.search + window.location.hash);
  };

  const value = useMemo(() => {
    const t = (path, fallback) => {
      const parts = path.split(".");
      let cur = dict[lang] || dict.ru;
      for (const p of parts) {
        if (cur == null) break;
        cur = cur[p];
      }
      if (cur == null && lang !== "ru") {
        cur = dict.ru;
        for (const p of parts) { if (cur == null) break; cur = cur[p]; }
      }
      return cur == null ? (fallback ?? path) : cur;
    };
    return { lang, setLang: setLangManual, t, langs: LANGS };
  }, [lang]);

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
};

export const useLang = () => useContext(LangCtx);
